// Finance-domain (expenses) HTTP service (Layer 2 — transport). Wraps
// lib/http.ts's request() for the Module 1 expense endpoints used by
// pages/Requests.tsx and pages/Approvals.tsx. Methods moved verbatim off
// ApiClient (apps/frontend/src/utils/api.ts), which now delegates here to
// preserve every existing `apiClient.*` call site.
import { request, API_BASE_URL } from '@/lib/http'
import { runAuthenticatedDownload } from '@/lib/downloads'
import type {
  ExpenseRequest, ApprovalDecision, Budget, BudgetDetail, BudgetScope, BudgetStatus, BudgetTrackingPeriod,
  ExpenseReceipt, Disbursement, Requisition, RequisitionType, FinanceOverview, ProjectBudgetPlan,
  ProjectBudgetPlanPayload, ProjectExpenseBudgetOption, ProjectExpenseBudgetChoice,
  ProjectBudgetReport, ProjectBudgetReportFilters, MyBudgetLine,
} from '@/types'

/** What a receipt hangs off (plan 043 D5): a refund claim, or a recorded
 *  disbursement. The two share one table, one upload flow and one download
 *  route; only the parent's URL segment differs. */
export type ReceiptParent = { kind: 'expense' | 'disbursement'; id: number }

function receiptsPath(parent: ReceiptParent) {
  return `/finance/${parent.kind === 'expense' ? 'expenses' : 'disbursements'}/${parent.id}/receipts`
}

export interface CreateExpensePayload {
  team_id: number | null; title: string; amount: string | number; currency?: string; category?: string
  description?: string; expense_date?: string
  // receipt_url is not accepted here (plan 027 design decision 7) — the
  // server ignores it now; receipts go through the presign/confirm pair
  // below, against the created expense's own id.
  project_id?: number | null; budget_id?: number | null
  budget_item_id?: number | null; budget_department_allocation_id?: number | null
}

export type UpdateExpensePayload = Partial<CreateExpensePayload>

export interface BudgetFilters {
  scope?: BudgetScope; team_id?: number; project_id?: number; status?: BudgetStatus; active_on?: string
  start_date?: string; end_date?: string
}

export type FinanceOverviewFilters = BudgetFilters

export interface CreateBudgetPayload {
  name: string; scope: BudgetScope; team_id?: number | null; project_id?: number | null
  period_start: string; period_end: string; budgeted_amount: string | number; notes?: string
  /** Plan 035 D1 — defaults to 'monthly' server-side when omitted. */
  tracking_period?: BudgetTrackingPeriod
}

export interface UpdateBudgetPayload {
  name?: string; period_start?: string; period_end?: string; budgeted_amount?: string | number
  status?: BudgetStatus; notes?: string
  /** Plan 035 D1 — the server 409s if this changes after plan buckets exist. */
  tracking_period?: BudgetTrackingPeriod
}

export interface BudgetLinePayload {
  category: string; budgeted_amount: string | number
}

export interface CreateDisbursementPayload {
  /** Null when paying an unbudgeted requisition (plan 034 D5). The server
   *  refuses a row with neither a budget nor a requisition. */
  budget_id?: number | null
  amount: string | number
  disbursed_to?: number
  note: string
  requisition_id?: number | null
  /** Pays an approved refund instead of a requisition (plan 034 D2). */
  expense_id?: number | null
  /** Plan 038 — plan-line coding for a direct disbursement against a project
   *  budget only. `budget_item_id` alone codes a fixed-cost line; both fields
   *  together code a department allocation. Omit both (and send `unplanned`
   *  instead) for a payment that isn't on the plan. Never sent alongside
   *  `requisition_id`/`expense_id`, or against a department budget. */
  budget_item_id?: number | null
  budget_department_allocation_id?: number | null
  /** Plan 038 D2 — the explicit "not on the plan" choice, mutually exclusive
   *  with the two fields above. */
  unplanned?: boolean
  /** Plan 038 D4 — `YYYY-MM-DD`, optional, defaults to today server-side. */
  paid_on?: string
}

export interface RequisitionFilters {
  type?: RequisitionType
  approval_status?: string
  team_id?: number
  /** Plan 039 D6b — with approval_status: 'approved', narrows the approvals
   *  queue to fund requisitions nobody has paid out yet. */
  unpaid?: boolean
}

export type CreateRequisitionPayload =
  | {
      type: 'fund'
      /** Null for a "General" requisition that belongs to no department (plan 043 D8). */
      team_id: number | null
      project_id?: number | null
      /** Null since plan 034 D5 — unbudgeted spend is recorded, not blocked. */
      budget_id?: number | null
      amount: string | number
      justification: string
    }
  | {
      type: 'budget'
      team_id: number
      name: string
      period_start: string
      period_end: string
      amount: string | number
      justification: string
    }

function budgetParams(filters?: BudgetFilters) {
  const params = new URLSearchParams()
  if (filters?.scope) params.set('scope', filters.scope)
  if (filters?.team_id) params.set('team_id', String(filters.team_id))
  if (filters?.project_id) params.set('project_id', String(filters.project_id))
  if (filters?.status) params.set('status', filters.status)
  if (filters?.active_on) params.set('active_on', filters.active_on)
  if (filters?.start_date) params.set('start_date', filters.start_date)
  if (filters?.end_date) params.set('end_date', filters.end_date)
  return params.toString()
}

function requisitionParams(filters?: RequisitionFilters) {
  const params = new URLSearchParams()
  if (filters?.type) params.set('type', filters.type)
  if (filters?.approval_status) params.set('approval_status', filters.approval_status)
  if (filters?.team_id) params.set('team_id', String(filters.team_id))
  if (filters?.unpaid) params.set('unpaid', '1')
  return params.toString()
}

function projectReportParams(filters: ProjectBudgetReportFilters, format?: 'csv') {
  const params = new URLSearchParams()
  params.set('budget_id', String(filters.budget_id))
  params.set('period', filters.period)
  if (filters.team_id != null) params.set('team_id', String(filters.team_id))
  if (filters.item_id != null) params.set('item_id', String(filters.item_id))
  if (filters.include_pending != null) params.set('include_pending', String(filters.include_pending))
  if (format) params.set('format', format)
  return params.toString()
}

export const financeApi = {
  getMyExpenses(status?: string): Promise<{ expense_requests: ExpenseRequest[] }> {
    return request(`/finance/expenses/mine${status ? `?status=${status}` : ''}`)
  },

  createExpense(payload: CreateExpensePayload, opId?: string): Promise<ExpenseRequest> {
    return request('/finance/expenses', {
      method: 'POST', body: JSON.stringify(payload),
      headers: opId ? { 'X-Op-Id': opId } : undefined,
    })
  },

  getTeamExpenses(teamId: number, status?: string): Promise<{ expense_requests: ExpenseRequest[] }> {
    return request(`/finance/expenses/team/${teamId}${status ? `?status=${status}` : ''}`)
  },

  getExpenseApprovals(
    params?: { approval_status?: 'approved'; unpaid?: boolean },
  ): Promise<{ expense_requests: ExpenseRequest[] }> {
    const qs = new URLSearchParams()
    if (params?.approval_status) qs.set('approval_status', params.approval_status)
    if (params?.unpaid) qs.set('unpaid', '1')
    const query = qs.toString()
    return request(`/finance/expenses/approvals${query ? `?${query}` : ''}`)
  },

  decideExpense(id: number, status: ApprovalDecision, notes?: string): Promise<ExpenseRequest> {
    return request(`/finance/expenses/${id}/decision`, { method: 'POST', body: JSON.stringify({ status, notes }) })
  },

  // ── Budgets (plan 026 / B4) ──────────────────────────────────────────────
  listBudgets(filters?: BudgetFilters): Promise<{ budgets: Budget[] }> {
    const qs = budgetParams(filters)
    return request(`/finance/budgets${qs ? `?${qs}` : ''}`)
  },

  updateExpense(id: number, payload: UpdateExpensePayload, opId?: string): Promise<ExpenseRequest> {
    return request(`/finance/expenses/${id}`, {
      method: 'PATCH', body: JSON.stringify(payload),
      headers: opId ? { 'X-Op-Id': opId } : undefined,
    })
  },

  listRequisitionBudgets(filters?: Pick<BudgetFilters, 'team_id'>): Promise<{ budgets: Budget[] }> {
    const qs = budgetParams(filters)
    return request(`/finance/requisition-budgets${qs ? `?${qs}` : ''}`)
  },

  getBudget(id: number): Promise<BudgetDetail> {
    return request(`/finance/budgets/${id}`)
  },

  createBudget(payload: CreateBudgetPayload): Promise<Budget> {
    return request('/finance/budgets', { method: 'POST', body: JSON.stringify(payload) })
  },

  updateBudget(id: number, payload: UpdateBudgetPayload): Promise<Budget> {
    return request(`/finance/budgets/${id}`, { method: 'PATCH', body: JSON.stringify(payload) })
  },

  /** Program manager signs a budget off, or sends it back (plan 034 D6). */
  decideBudget(id: number, status: ApprovalDecision, notes?: string): Promise<Budget> {
    return request(`/finance/budgets/${id}/decision`, {
      method: 'POST',
      body: JSON.stringify({ status, notes }),
    })
  },

  deleteBudget(id: number): Promise<void> {
    return request(`/finance/budgets/${id}`, { method: 'DELETE' })
  },

  replaceBudgetLines(id: number, lines: BudgetLinePayload[]): Promise<{ lines: BudgetDetail['lines'] }> {
    return request(`/finance/budgets/${id}/lines`, { method: 'PUT', body: JSON.stringify({ lines }) })
  },

  getProjectBudgetPlan(id: number): Promise<ProjectBudgetPlan> {
    return request(`/finance/budgets/${id}/project-plan`)
  },

  replaceProjectBudgetPlan(id: number, payload: ProjectBudgetPlanPayload): Promise<ProjectBudgetPlan> {
    return request(`/finance/budgets/${id}/project-plan`, { method: 'PUT', body: JSON.stringify(payload) })
  },

  getExpenseBudgetOptions(
    projectId: number,
    expenseDate: string,
  ): Promise<{ options: ProjectExpenseBudgetOption[]; budgets: ProjectExpenseBudgetChoice[] }> {
    const params = new URLSearchParams({ project_id: String(projectId), expense_date: expenseDate })
    return request(`/finance/expense-budget-options?${params.toString()}`)
  },

  getProjectBudgetReport(filters: ProjectBudgetReportFilters): Promise<ProjectBudgetReport> {
    return request(`/finance/project-budget-report?${projectReportParams(filters)}`)
  },

  downloadProjectBudgetReportCsv(filters: ProjectBudgetReportFilters): Promise<void> {
    return runAuthenticatedDownload(
      `${API_BASE_URL}/finance/project-budget-report?${projectReportParams(filters, 'csv')}`,
      `project-budget-variance-${filters.period}.csv`,
    )
  },

  /** Plan 035 D7 — the whole plan in Toju's planning layout, as a workbook. */
  downloadBudgetPlanningExport(budgetId: number, includePending: boolean): Promise<void> {
    return runAuthenticatedDownload(
      `${API_BASE_URL}/finance/budgets/${budgetId}/planning-export?include_pending=${includePending ? 1 : 0}`,
      `budget-${budgetId}-planning.xlsx`,
    )
  },

  // ── Budget CSV (plan 034 D10) ───────────────────────────────────────────
  // The template comes FROM the server, pre-filled with the current plan, so
  // no column name is ever spelled on the client — Toju can swap the template
  // by changing one backend file.
  downloadProjectPlanCsvTemplate(budgetId: number): Promise<void> {
    return runAuthenticatedDownload(
      `${API_BASE_URL}/finance/budgets/${budgetId}/project-plan/csv-template`,
      `budget-${budgetId}-plan.csv`,
    )
  },

  uploadProjectPlanCsv(budgetId: number, file: File): Promise<ProjectBudgetPlan> {
    const body = new FormData()
    body.append('file', file)
    return request(`/finance/budgets/${budgetId}/project-plan/csv`, { method: 'POST', body })
  },

  downloadBudgetLinesCsvTemplate(budgetId: number): Promise<void> {
    return runAuthenticatedDownload(
      `${API_BASE_URL}/finance/budgets/${budgetId}/lines/csv-template`,
      `budget-${budgetId}-categories.csv`,
    )
  },

  uploadBudgetLinesCsv(budgetId: number, file: File): Promise<{ lines: BudgetDetail['lines'] }> {
    const body = new FormData()
    body.append('file', file)
    return request(`/finance/budgets/${budgetId}/lines/csv`, { method: 'POST', body })
  },

  getOverview(filters?: FinanceOverviewFilters): Promise<FinanceOverview> {
    const qs = budgetParams(filters)
    return request(`/finance/overview${qs ? `?${qs}` : ''}`)
  },

  createDisbursement(payload: CreateDisbursementPayload): Promise<Disbursement> {
    return request('/finance/disbursements', { method: 'POST', body: JSON.stringify(payload) })
  },

  listDisbursements(filters?: BudgetFilters): Promise<{ disbursements: Disbursement[] }> {
    const qs = budgetParams(filters)
    return request(`/finance/disbursements${qs ? `?${qs}` : ''}`)
  },

  listBudgetDisbursements(id: number): Promise<{ disbursements: Disbursement[] }> {
    return request(`/finance/budgets/${id}/disbursements`)
  },

  createRequisition(payload: CreateRequisitionPayload): Promise<Requisition> {
    return request('/finance/requisitions', { method: 'POST', body: JSON.stringify(payload) })
  },

  getMyRequisitions(filters?: RequisitionFilters): Promise<{ requisitions: Requisition[] }> {
    const qs = requisitionParams(filters)
    return request(`/finance/requisitions/mine${qs ? `?${qs}` : ''}`)
  },

  getRequisitionApprovals(filters?: RequisitionFilters): Promise<{ requisitions: Requisition[] }> {
    const qs = requisitionParams(filters)
    return request(`/finance/requisitions/approvals${qs ? `?${qs}` : ''}`)
  },

  decideRequisition(id: number, status: ApprovalDecision, notes?: string): Promise<Requisition> {
    return request(`/finance/requisitions/${id}/decision`, { method: 'POST', body: JSON.stringify({ status, notes }) })
  },

  getMyBudgetLines(): Promise<{ lines: MyBudgetLine[] }> {
    return request('/finance/my-budget-lines')
  },

  // ── Receipts (plan 027 / B2; disbursements since plan 043 D5) ───────────
  presignReceiptFor(
    parent: ReceiptParent,
    meta: { file_name: string; content_type: string; size_bytes: number },
  ): Promise<{ upload_url: string; file_key: string; storage_backend: string }> {
    return request(`${receiptsPath(parent)}/presign`, { method: 'POST', body: JSON.stringify(meta) })
  },

  confirmReceiptFor(
    parent: ReceiptParent,
    payload: { file_key: string; file_name: string; content_type: string; size_bytes: number },
    opId?: string,
  ): Promise<ExpenseReceipt> {
    return request(receiptsPath(parent), {
      method: 'POST', body: JSON.stringify(payload),
      headers: opId ? { 'X-Op-Id': opId } : undefined,
    })
  },

  listReceiptsFor(parent: ReceiptParent): Promise<{ items: ExpenseReceipt[] }> {
    return request(receiptsPath(parent))
  },

  // The expense-only forms the offline queue (offline/sync.ts) and the refund
  // form already call.
  presignReceipt(
    expenseId: number,
    meta: { file_name: string; content_type: string; size_bytes: number },
  ): Promise<{ upload_url: string; file_key: string; storage_backend: string }> {
    return financeApi.presignReceiptFor({ kind: 'expense', id: expenseId }, meta)
  },

  confirmReceipt(
    expenseId: number,
    payload: { file_key: string; file_name: string; content_type: string; size_bytes: number },
    opId?: string,
  ): Promise<ExpenseReceipt> {
    return financeApi.confirmReceiptFor({ kind: 'expense', id: expenseId }, payload, opId)
  },

  deleteReceipt(receiptId: number): Promise<void> {
    return request(`/finance/receipts/${receiptId}`, { method: 'DELETE' })
  },

  downloadReceipt(receiptId: number, fileName: string): Promise<void> {
    return runAuthenticatedDownload(`${API_BASE_URL}/finance/receipts/${receiptId}/download`, fileName)
  },
}
